import { Controller } from '@hotwired/stimulus';
import { trapFocus, visibleFocusables } from '../utils/focus_trap.js';

/**
 * Sidebar Controller
 *
 * Manages responsive sidebar behavior: hidden on mobile, slide-in drawer via hamburger.
 * Auto-closes on navigation (turbo:before-visit) and Escape key.
 *
 * Shared by the app (`application`), `admin` and `contractor_space` layouts. Refonte de
 * l'espace chantier V2 (G-10), changes kept backward compatible for all three:
 * - the Escape guard only yields to a dialog that is RENDERED and is not the sidebar
 *   itself (`otherDialogOpen`): a closed slide-over or modal (role="dialog" under a
 *   `hidden` host) used to swallow Escape, and the app's « Menu » sheet now carries
 *   `role="dialog"` on the `<aside>` (set by `sidebar_collapse_controller.js`);
 * - the Tab trap goes through `utils/focus_trap.js`; a Tab leaving a dropdown portal (the profile
 *   menu, portalled INTO the open sheet) stays in the sheet (`tabOutOfDropdownPortal`, shared with
 *   `sidebar_collapse_controller.js`); an Escape already handled (`defaultPrevented`, the profile
 *   menu closing) does not close the sheet;
 * - closing hands the focus back to the opener (except on a Turbo visit), and an opener
 *   that already declares `aria-expanded` gets it kept in sync (the app's phone bar
 *   « Menu » button; the admin and contractor openers have none and stay untouched);
 * - `sidebar:opened` / `sidebar:closed` are dispatched on the sidebar target, so a
 *   controller mounted ON the `<aside>` (`sidebar-collapse`) can follow the drawer.
 *
 * Targets:
 * - sidebar: The aside element
 * - backdrop: The overlay behind the sidebar
 *
 * Values:
 * - open: Whether the sidebar is currently visible on mobile (default: false)
 *
 * @example
 * <div data-controller="sidebar">
 *   <div data-sidebar-target="backdrop" data-action="click->sidebar#close"></div>
 *   <aside data-sidebar-target="sidebar">...</aside>
 *   <button data-action="click->sidebar#open">Menu</button>
 * </div>
 */

const DIALOG_SELECTOR =
  '[role="dialog"]:not(.hidden), [data-command-palette-target="dialog"]:not(.hidden)';

// Rendu = au moins une boîte. Un dialogue sous un hôte `hidden` (slide-over ou modale fermés)
// n'en a aucune, même si lui-même ne porte pas `.hidden`.
function isRendered(element) {
  if (typeof element.checkVisibility === 'function') return element.checkVisibility();
  return element.getClientRects().length > 0;
}

/**
 * Un dialogue AUTRE que `container` (et que ses descendants) est-il affiché ?
 *
 * Garde d'Échap et de raccourci du menu : la palette de commandes, une modale ou un
 * slide-over OUVERTS gardent la main. Le menu lui-même (`role="dialog"` de la feuille et de
 * la superposition) ne compte pas, sinon Échap ne fermerait plus jamais le menu ouvert.
 *
 * @param {Element|null} container - l'`<aside>` du menu
 * @param {Document} [root]
 * @returns {boolean}
 */
export function otherDialogOpen(container, root = document) {
  return Array.from(root.querySelectorAll(DIALOG_SELECTOR)).some(
    (dialog) => !container?.contains(dialog) && isRendered(dialog)
  );
}

const DROPDOWN_PORTAL_SELECTOR = '[data-dropdown-portal]';

/**
 * Le nœud est-il dans le portail d'un dropdown (menu du profil) ?
 *
 * Le menu ouvert en dialogue (feuille « Menu », superposition tablette) porte ce portail DANS
 * l'`<aside>` (`portalHost` de `dropdown_controller.js`). Un Tab dans le menu le FERME : le portail
 * sort du DOM pendant le même `keydown`. Le nœud peut donc être détaché : `closest` remonte quand
 * même jusqu'au portail.
 *
 * @param {EventTarget|null} node
 * @returns {boolean}
 */
export function inDropdownPortal(node) {
  return Boolean(node?.closest?.(DROPDOWN_PORTAL_SELECTOR));
}

// `dropdown_controller.js#createPortal` : le menu porté a l'id `<id du menu>-portal`, et le déclencheur
// pointe dessus par `aria-controls` tant qu'il est ouvert, puis sur `<id du menu>` après `close()`.
const PORTAL_MENU_SUFFIX = '-portal';

function dropdownTrigger(portal, focusables) {
  const portalMenuId = portal.firstElementChild?.getAttribute?.('id') || '';
  if (!portalMenuId.endsWith(PORTAL_MENU_SUFFIX)) return null;
  const ids = [portalMenuId, portalMenuId.slice(0, -PORTAL_MENU_SUFFIX.length)];
  return focusables.find((el) => ids.includes(el.getAttribute('aria-controls'))) ?? null;
}

/**
 * Tab ou Maj+Tab parti du menu d'un dropdown porté DANS le menu ouvert en dialogue (feuille
 * « Menu », superposition tablette). Partagé avec `sidebar_collapse_controller.js`.
 *
 * `dropdown#onPortalKeydown` ferme le menu sur Tab sans retenir la tabulation native. Le portail est
 * le DERNIER enfant de l'`<aside>` : la tabulation native en sortait vers la page derrière le dialogue
 * modal (à 390 px, la cloche de la barre du téléphone ; QA de la PR 1, F3). Le menu se lit donc comme
 * s'il suivait son déclencheur : Tab va au focalisable qui SUIT le déclencheur (rebouclé sur le premier),
 * Maj+Tab revient au déclencheur. Sans déclencheur reconnu : premier (Tab) ou dernier (Maj+Tab)
 * focalisable du conteneur. Les items d'un portail encore attaché ne comptent pas.
 *
 * @param {HTMLElement} container - l'`<aside>` ouvert en dialogue
 * @param {KeyboardEvent} event - `keydown` Tab
 * @returns {boolean} `true` si la tabulation venait d'un portail (et a été prise en charge)
 */
export function tabOutOfDropdownPortal(container, event) {
  const portal = event.target?.closest?.(DROPDOWN_PORTAL_SELECTOR);
  if (!portal) return false;

  event.preventDefault();
  const focusables = visibleFocusables(container).filter((el) => !inDropdownPortal(el));
  if (focusables.length === 0) {
    container.focus?.();
    return true;
  }

  const trigger = dropdownTrigger(portal, focusables);
  let next;
  if (!trigger) next = event.shiftKey ? focusables[focusables.length - 1] : focusables[0];
  else if (event.shiftKey) next = trigger;
  else next = focusables[focusables.indexOf(trigger) + 1] ?? focusables[0];
  next.focus();
  return true;
}

export default class extends Controller {
  static targets = ['sidebar', 'backdrop'];

  static values = {
    open: { type: Boolean, default: false },
  };

  connect() {
    this._hasBeenOpened = false;
    this.opener = null;
    this.restoreFocusOnHide = true;
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.boundHandleNavigation = this.close.bind(this);
    document.addEventListener('keydown', this.boundHandleKeydown);
    document.addEventListener('turbo:before-visit', this.boundHandleNavigation);
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundHandleKeydown);
    document.removeEventListener('turbo:before-visit', this.boundHandleNavigation);
    // Clean up body overflow if sidebar was open when removed from DOM
    if (this.openValue) {
      document.body.classList.remove('overflow-hidden');
    }
  }

  open(event) {
    // Le déclencheur, pour lui rendre le focus et tenir son `aria-expanded`. Sans événement
    // (appel programmatique), l'élément focalisé, s'il y en a un.
    const trigger = event?.currentTarget;
    this.opener =
      trigger && typeof trigger.focus === 'function'
        ? trigger
        : document.activeElement !== document.body
          ? document.activeElement
          : null;
    this.restoreFocusOnHide = true;
    this.openValue = true;
  }

  close(event) {
    // Une visite Turbo remplace la page : rendre le focus au bouton « Menu » n'a pas de sens.
    // Tiroir fermé, on n'y touche pas : `hideSidebar` ne tournera pas pour le remettre, et une
    // visite annulée (garde `turbo:before-visit`) laisserait la page avec un drapeau périmé.
    if (this.openValue) this.restoreFocusOnHide = event?.type !== 'turbo:before-visit';
    this.openValue = false;
  }

  /**
   * Callback when openValue changes — applies CSS classes for slide-in/out.
   * @param {boolean} isOpen
   */
  openValueChanged(isOpen) {
    if (isOpen) {
      this._hasBeenOpened = true;
      this.showSidebar();
    } else if (this._hasBeenOpened) {
      // Only run hideSidebar if the sidebar was actually opened before.
      // Prevents the initial default-value callback (open=false) from
      // stripping overflow-hidden off the body on page load.
      this.hideSidebar();
    }
  }

  // ── Private ──

  /**
   * @private
   */
  showSidebar() {
    // Show backdrop
    this.backdropTarget.classList.remove('hidden');
    // Force reflow for transition
    void this.backdropTarget.offsetHeight;
    this.backdropTarget.classList.remove('opacity-0');
    this.backdropTarget.classList.add('opacity-100');

    // Slide sidebar in
    this.sidebarTarget.classList.remove('-translate-x-full');
    this.sidebarTarget.classList.add('translate-x-0');
    this.sidebarTarget.setAttribute('aria-modal', 'true');
    this.syncOpenerExpanded(true);
    // Avant la mise au point ci-dessous : `sidebar-collapse` retire `inert` de l'`<aside>`.
    this.dispatch('opened', { target: this.sidebarTarget });

    // Lock body scroll
    document.body.classList.add('overflow-hidden');

    // Install focus trap
    this.boundHandleTab = this.handleTabKey.bind(this);
    this.sidebarTarget.addEventListener('keydown', this.boundHandleTab);

    // Focus first focusable element in sidebar
    requestAnimationFrame(() => {
      const focusable = this.sidebarTarget.querySelector(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable) focusable.focus();
    });
  }

  /**
   * @private
   */
  hideSidebar() {
    // Fade backdrop
    this.backdropTarget.classList.remove('opacity-100');
    this.backdropTarget.classList.add('opacity-0');

    // Slide sidebar out
    this.sidebarTarget.classList.remove('translate-x-0');
    this.sidebarTarget.classList.add('-translate-x-full');
    this.sidebarTarget.removeAttribute('aria-modal');
    this.syncOpenerExpanded(false);

    // Unlock body scroll
    document.body.classList.remove('overflow-hidden');

    // Remove focus trap
    if (this.boundHandleTab) {
      this.sidebarTarget.removeEventListener('keydown', this.boundHandleTab);
    }

    this.restoreFocus();
    this.dispatch('closed', { target: this.sidebarTarget });

    // Hide backdrop after transition
    setTimeout(() => {
      if (!this.openValue) {
        this.backdropTarget.classList.add('hidden');
      }
    }, 200);
  }

  /**
   * Focus back to the opener, only when it would otherwise be lost: still in the closing
   * sidebar, or on `<body>`. A drawer item that opened a modal keeps the modal's focus.
   * @private
   */
  restoreFocus() {
    const restore = this.restoreFocusOnHide;
    this.restoreFocusOnHide = true;
    const opener = this.opener;
    if (!restore || !opener?.isConnected) return;

    const active = document.activeElement;
    if (active && active !== document.body && !this.sidebarTarget.contains(active)) return;
    opener.focus();
  }

  /**
   * `aria-expanded` of an opener that declares it (app phone bar « Menu »). An opener
   * without the attribute does not gain one.
   * @param {boolean} expanded
   * @private
   */
  syncOpenerExpanded(expanded) {
    if (this.opener?.hasAttribute?.('aria-expanded')) {
      this.opener.setAttribute('aria-expanded', String(expanded));
    }
  }

  /**
   * Handles Escape key — closes sidebar if no other dialog is open.
   * @param {KeyboardEvent} event
   * @private
   */
  handleKeydown(event) {
    if (event.key !== 'Escape' || !this.openValue) return;

    // Déjà traité plus bas dans l'arbre : le menu du profil porté dans la feuille ferme sur
    // Échap et fait `preventDefault()` ; la feuille, elle, reste ouverte.
    if (event.defaultPrevented) return;

    // Guard: don't close if a dialog (command palette, modal) is open — a RENDERED one,
    // other than the sidebar itself.
    if (otherDialogOpen(this.sidebarTarget)) return;

    event.preventDefault();
    this.close(event);
  }

  /**
   * Traps Tab focus within sidebar when open (a11y).
   * @param {KeyboardEvent} event
   * @private
   */
  handleTabKey(event) {
    if (event.key !== 'Tab') return;
    if (tabOutOfDropdownPortal(this.sidebarTarget, event)) return;
    trapFocus(this.sidebarTarget, event);
  }
}
